import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { CalendarRange, Plus, Trash2 } from "lucide-react";

const DAYS = [
  { v: 0, label: "Mon" }, { v: 1, label: "Tue" }, { v: 2, label: "Wed" },
  { v: 3, label: "Thu" }, { v: 4, label: "Fri" }, { v: 5, label: "Sat" },
  { v: 6, label: "Sun" },
];

/**
 * ScheduleManager — manages per-org weekly schedules for a doctor.
 * @param api the authenticated axios instance from useAuth()
 * @param orgs an array of org options [{id, name}] the doctor can attach schedules to
 */
export default function ScheduleManager({ api, orgs = [] }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/doctors/me/schedules");
      setRows(data.schedules || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const addRow = () => {
    setRows((r) => [...r, {
      org_id: orgs[0]?.id || "",
      day_of_week: 0,
      start_time: "09:00",
      end_time: "13:00",
      slot_duration: 10,
    }]);
  };

  const updateRow = (idx, patch) => {
    setRows((r) => r.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  };

  const removeRow = (idx) => {
    setRows((r) => r.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Basic validation
      for (const r of rows) {
        if (!r.org_id) { toast.error("Pick a clinic for every row"); setSaving(false); return; }
        if (r.start_time >= r.end_time) { toast.error("End time must be after start time"); setSaving(false); return; }
      }
      await api.put("/doctors/me/schedules", { schedules: rows });
      toast.success("Schedules saved");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 h-12" data-testid="open-schedule-manager">
          <CalendarRange className="w-4 h-4" /> My Schedule
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="schedule-manager-dialog">
        <DialogHeader>
          <DialogTitle>Weekly schedule (multi-clinic)</DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {rows.length === 0 && (
              <p className="text-sm text-slate-500" data-testid="schedule-empty">No rows yet. Click “Add row” to add a clinic block.</p>
            )}
            {rows.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 rounded-lg border border-slate-200" data-testid={`schedule-row-${idx}`}>
                <div className="col-span-4">
                  <Label className="text-xs">Clinic</Label>
                  <Select value={r.org_id} onValueChange={(v) => updateRow(idx, { org_id: v })}>
                    <SelectTrigger data-testid={`schedule-row-org-${idx}`}><SelectValue placeholder="Clinic" /></SelectTrigger>
                    <SelectContent>
                      {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Day</Label>
                  <Select value={String(r.day_of_week)} onValueChange={(v) => updateRow(idx, { day_of_week: parseInt(v) })}>
                    <SelectTrigger data-testid={`schedule-row-day-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Start</Label>
                  <Input type="time" value={r.start_time} onChange={(e) => updateRow(idx, { start_time: e.target.value })} data-testid={`schedule-row-start-${idx}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">End</Label>
                  <Input type="time" value={r.end_time} onChange={(e) => updateRow(idx, { end_time: e.target.value })} data-testid={`schedule-row-end-${idx}`} />
                </div>
                <div className="col-span-1">
                  <Label className="text-xs">Slot</Label>
                  <Input type="number" min="5" max="120" value={r.slot_duration} onChange={(e) => updateRow(idx, { slot_duration: parseInt(e.target.value) || 10 })} data-testid={`schedule-row-slot-${idx}`} />
                </div>
                <div className="col-span-1">
                  <Button variant="ghost" size="icon" onClick={() => removeRow(idx)} data-testid={`schedule-row-delete-${idx}`}>
                    <Trash2 className="w-4 h-4 text-slate-500" />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addRow} className="gap-1" data-testid="schedule-add-row">
              <Plus className="w-3.5 h-3.5" /> Add row
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="schedule-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
